import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import DataTable from 'react-data-table-component'
import moment from 'moment'
import { Link, useNavigate } from 'react-router-dom'
import { useModuleSettings } from '../../context/adminSettingsContext'
import { hasPermission } from '../../common/permissions'
import Swal from 'sweetalert2'
import { allCouponCodes, changeCouponCodeStatus, deleteCouponCodes } from '../../slice/couponCodeSlice'

const CouponCodeList = () => {
    const dispatch = useDispatch()
    const { couponCodeList } = useSelector(state => state.couponCode)
    const navigate = useNavigate()

    const {
        page,
        setPage,
        limit,
        setLimit,
    } = useModuleSettings()

    const [statusFilter, setStatusFilter] = useState('all')
    const [searchQuery, setSearchQuery] = useState('')
    const [searchDebounce, setSearchDebounce] = useState('')

    // Debounce search input
    useEffect(() => {
        const timer = setTimeout(() => {
            setSearchDebounce(searchQuery)
            setPage(1)
        }, 500)

        return () => clearTimeout(timer)
    }, [searchQuery])

    useEffect(() => {
        dispatch(allCouponCodes({
            page,
            limit,
            filter: statusFilter !== 'all' ? statusFilter : undefined,
            search: searchDebounce || undefined
        }))
    }, [dispatch, page, limit, statusFilter, searchDebounce])

    // Toggle status handler
    const handleToggleStatus = async (couponId) => {
        try {
            const response = await dispatch(changeCouponCodeStatus({
                id: couponId
            }))
            if (response?.payload?.code == 200) {
                Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'success',
                    backdrop: false,
                    title: response?.payload?.message,
                    showConfirmButton: false,
                    timer: 1500,
                })
            }
            dispatch(allCouponCodes({
                page,
                limit,
                filter: statusFilter !== 'all' ? statusFilter : undefined,
                search: searchDebounce || undefined
            }))
        } catch (error) {
            console.error('Error toggling coupon status:', error)
        }
    }

    // Delete handler
    const handleDelete = async (couponId) => {
        const result = await Swal.fire({
            title: 'Delete Coupon?',
            text: 'This action cannot be undone.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Yes, delete',
            cancelButtonText: 'Cancel',
            reverseButtons: true,
        })

        if (!result.isConfirmed) return

        try {
            const deleteResponse = await dispatch(deleteCouponCodes({
                id: couponId
            }))

            if (deleteResponse?.payload?.code == 200) {
                Swal.fire({
                    icon: 'success',
                    title: 'Deleted',
                    text: deleteResponse?.payload?.message,
                    timer: 1500,
                    showConfirmButton: false,
                })
            }

            dispatch(
                allCouponCodes({
                    page,
                    limit,
                    filter: statusFilter !== 'all' ? statusFilter : undefined,
                    search: searchDebounce || undefined
                })
            )
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Failed',
                text: error || 'Failed to delete coupon.',
            })
        }
    }

    const handleView = (couponId) => {
        navigate('/coupons/view', {
            state: { couponId }
        })
    }

    const handleEdit = (couponId) => {
        navigate('/coupons/edit', {
            state: { couponId }
        })
    }

    // Check if coupon is expired
    const isExpired = (validTo) => {
        return moment(validTo).isBefore(moment())
    }

    // Check if coupon is upcoming
    const isUpcoming = (validFrom) => {
        return moment(validFrom).isAfter(moment())
    }

    // Get validity status badge
    const getValidityBadge = (validFrom, validTo) => {
        if (isExpired(validTo)) {
            return (
                <span className="badge bg-danger">
                    <i className="fe-x-circle" style={{ fontSize: '10px' }}></i> Expired
                </span>
            )
        } else if (isUpcoming(validFrom)) {
            return (
                <span className="badge bg-info">
                    <i className="fe-clock" style={{ fontSize: '10px' }}></i> Upcoming
                </span>
            )
        } else {
            return (
                <span className="badge bg-success">
                    <i className="fe-check-circle" style={{ fontSize: '10px' }}></i> Active
                </span>
            )
        }
    }

    const columns = [
        {
            name: 'Sr No',
            width: '80px',
            selector: (_, index) =>
                ((couponCodeList?.paging?.page_no || 1) - 1) * limit + index + 1,
        },
        {
            name: 'Coupon ID',
            selector: row => row.id,
            sortable: true,
            width: '100px',
        },
        {
            name: 'Coupon Code',
            selector: row => row.name,
            sortable: true,
            minWidth: '150px',
            cell: row => (
                <div>
                    <span
                        style={{
                            fontWeight: '700',
                            color: '#495057',
                            fontSize: '13px',
                            fontFamily: 'monospace',
                            backgroundColor: '#f8f9fa',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            border: '1px dashed #dee2e6'
                        }}
                    >
                        {row.name || 'NA'}
                    </span>
                </div>
            )
        },
        {
            name: 'Description',
            selector: row => row.description || 'NA',
            sortable: true,
            minWidth: '300px',
            cell: row => (
                <div
                    style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                    }}
                    title={row.description}
                >
                    {row.description || 'NA'}
                </div>
            )
        },
        {
            name: 'Discount',
            selector: row => row.discount_percent,
            sortable: true,
            width: '120px',
            center: true,
            cell: row => (
                <span
                    style={{
                        fontWeight: '700',
                        color: '#28a745',
                        fontSize: '15px',
                        backgroundColor: '#d4edda',
                        padding: '6px 12px',
                        borderRadius: '6px',
                        border: '1px solid #c3e6cb'
                    }}
                >
                    {row.discount_percent}% OFF
                </span>
            )
        },
        {
            name: 'Valid From',
            selector: row =>
                row.valid_from
                    ? moment(row.valid_from).format('DD MMM YYYY')
                    : 'NA',
            sortable: true,
            minWidth: '140px',
        },
        {
            name: 'Valid To',
            selector: row =>
                row.valid_to
                    ? moment(row.valid_to).format('DD MMM YYYY')
                    : 'NA',
            sortable: true,
            minWidth: '140px',
        },
        {
            name: 'Validity',
            width: '130px',
            center: true,
            cell: row => getValidityBadge(row.valid_from, row.valid_to)
        },
        {
            name: 'Created Date',
            selector: row =>
                row.createdAt
                    ? moment(row.createdAt).local().format('DD MMM YYYY')
                    : 'NA',
            sortable: true,
            minWidth: '140px',
        },
        {
            name: 'Status',
            width: '100px',
            center: true,
            cell: row => (
                <div className="form-check form-switch">
                    <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        id={`switch-${row.id}`}
                        checked={row.is_active === 1}
                        onChange={() => handleToggleStatus(row.id)}
                        disabled={!hasPermission('coupons', 'edit') || row.is_deleted === 1}
                        style={{
                            cursor: hasPermission('coupons', 'edit') && row.is_deleted !== 1 ? 'pointer' : 'not-allowed',
                            opacity: row.is_deleted === 1 ? 0.5 : 1
                        }}
                    />
                </div>
            ),
        },
        {
            name: 'Action',
            width: '150px',
            center: true,
            cell: row => (
                <div className="d-flex gap-2 justify-content-center">
                    {hasPermission('coupons', 'view') && (
                        <button
                            className="btn btn-sm btn-outline-primary"
                            title="View"
                            onClick={() => handleView(row.id)}
                            style={{
                                width: '32px',
                                height: '32px',
                                padding: '0',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: '1px solid #dee2e6',
                                borderRadius: '4px'
                            }}
                        >
                            <i className="fe-eye" style={{ fontSize: '14px' }} />
                        </button>
                    )}

                    {hasPermission('coupons', 'edit') && (
                        <button
                            className="btn btn-sm btn-outline-warning"
                            title="Edit"
                            onClick={() => handleEdit(row.id)}
                            style={{
                                width: '32px',
                                height: '32px',
                                padding: '0',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: '1px solid #dee2e6',
                                borderRadius: '4px'
                            }}
                        >
                            <i className="fe-edit" style={{ fontSize: '14px' }} />
                        </button>
                    )}

                    {hasPermission('coupons', 'delete') && row.is_deleted !== 1 && (
                        <button
                            className="btn btn-sm btn-outline-danger"
                            title="Delete"
                            onClick={() => handleDelete(row.id)}
                            style={{
                                width: '32px',
                                height: '32px',
                                padding: '0',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: '1px solid #dc3545',
                                borderRadius: '4px'
                            }}
                        >
                            <i className="fe-trash-2" style={{ fontSize: '14px' }} />
                        </button>
                    )}
                </div>
            ),
        },
    ]

    const customStyles = {
        table: {
            style: {
                backgroundColor: '#ffffff',
                borderRadius: '8px',
            },
        },
        headRow: {
            style: {
                backgroundColor: '#f8f9fa',
                borderBottom: '2px solid #dee2e6',
                minHeight: '48px',
            },
        },
        headCells: {
            style: {
                fontSize: '13px',
                fontWeight: '600',
                color: '#495057',
                paddingLeft: '12px',
                paddingRight: '12px',
            },
        },
        cells: {
            style: {
                fontSize: '13px',
                color: '#212529',
                paddingTop: '12px',
                paddingBottom: '12px',
                paddingLeft: '12px',
                paddingRight: '12px',
            },
        },
        rows: {
            style: {
                minHeight: '60px',
                '&:hover': {
                    backgroundColor: '#f8f9fa',
                    cursor: 'pointer',
                },
            },
            stripedStyle: {
                backgroundColor: '#ffffff',
            },
        },
        pagination: {
            style: {
                borderTop: '1px solid #dee2e6',
                minHeight: '56px',
                fontSize: '13px',
            },
        },
    }

    return (
        <div className="container-fluid px-4 py-3">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="mb-0 fw-semibold">Coupon Codes</h4>

                {hasPermission('coupons', 'add') && (
                    <Link
                        to="/coupons/add"
                        className="btn btn-success"
                        style={{
                            padding: '8px 20px',
                            fontSize: '14px',
                            fontWeight: '500',
                            borderRadius: '6px'
                        }}
                    >
                        + Add Coupon
                    </Link>
                )}
            </div>

            {/* Search and Filter */}
            <div className="card border-0 shadow-sm mb-3" style={{ borderRadius: '8px' }}>
                <div className="card-body p-3">
                    <div className="row g-3">
                        <div className="col-md-8">
                            <div className="position-relative">
                                <i
                                    className="fe-search position-absolute"
                                    style={{
                                        left: '12px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        color: '#6c757d',
                                        fontSize: '16px'
                                    }}
                                />
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search by coupon code or description..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    style={{
                                        paddingLeft: '38px',
                                        paddingRight: searchQuery ? '38px' : '12px',
                                        height: '40px',
                                        fontSize: '14px',
                                        border: '1px solid #dee2e6',
                                        borderRadius: '6px'
                                    }}
                                />
                                {searchQuery && (
                                    <button
                                        className="btn btn-link position-absolute p-0"
                                        type="button"
                                        onClick={() => setSearchQuery('')}
                                        style={{
                                            right: '12px',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            color: '#6c757d'
                                        }}
                                    >
                                        <i className="fe-x" style={{ fontSize: '16px' }}></i>
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className="col-md-4">
                            <select
                                className="form-select"
                                value={statusFilter}
                                onChange={(e) => {
                                    setStatusFilter(e.target.value)
                                    setPage(1)
                                }}
                                style={{
                                    height: '40px',
                                    fontSize: '14px',
                                    border: '1px solid #dee2e6',
                                    borderRadius: '6px'
                                }}
                            >
                                <option value="all">All</option>
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                                <option value="deleted">Deleted</option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="card border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                <DataTable
                    columns={columns}
                    data={couponCodeList?.data || []}
                    pagination
                    paginationServer
                    paginationTotalRows={couponCodeList?.paging?.total || 0}
                    paginationPerPage={limit}
                    paginationDefaultPage={couponCodeList?.paging?.page_no || 1}
                    onChangeRowsPerPage={(newLimit) => {
                        setLimit(newLimit)
                        setPage(1)
                    }}
                    onChangePage={(newPage) => setPage(newPage)}
                    responsive
                    highlightOnHover
                    persistTableHead
                    progressPending={couponCodeList?.loading}
                    noDataComponent={
                        <div className="p-5 text-center">
                            <i className="fe-tag" style={{ fontSize: '48px', color: '#adb5bd' }}></i>
                            <h6 className="mt-3 mb-1 text-muted">
                                {searchQuery ? 'No coupons found matching your search' : 'No coupons found'}
                            </h6>
                            <p className="text-muted small mb-0">
                                {searchQuery ? 'Try adjusting your search criteria' : 'Get started by adding a new coupon'}
                            </p>
                        </div>
                    }
                    customStyles={customStyles}
                />
            </div>
        </div>
    )
}

export default CouponCodeList;